// src/components/auth/SignupForm.tsx
// New account creation form. Displayed on the "signup" tab of AuthModal.
// Includes Google Sign-Up and password-strength indicator.

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAuthStore } from '../../stores/useAuthStore';
import {
  handleSignup,
  handleGoogleSignIn,
  handleGoogleError,
  getAuthErrorMessage,
} from '../../services/authService';

const C = {
  accent:  '#4f8cf7',
  text:    '#f1f5f9',
  muted:   '#8b9cc4',
  error:   '#f87171',
  success: '#34d399',
  warn:    '#fbbf24',
};

const INPUT_BASE: React.CSSProperties = {
  width: '100%', padding: '12px 14px',
  background: 'rgba(255,255,255,0.04)',
  border: '1.5px solid rgba(255,255,255,0.08)',
  borderRadius: '10px', color: C.text,
  fontSize: '0.95rem', outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  fontFamily: 'inherit',
};

function msgStyle(type: string): React.CSSProperties {
  const map: Record<string, [string, string]> = {
    error:   ['rgba(248,113,113,0.1)',  'rgba(248,113,113,0.25)'],
    success: ['rgba(52,211,153,0.1)',   'rgba(52,211,153,0.25)'],
    info:    ['rgba(79,140,247,0.1)',   'rgba(79,140,247,0.25)'],
  };
  const [bg, border] = map[type] ?? map.info;
  return {
    padding: '11px 14px', borderRadius: '10px', fontSize: '0.88rem',
    background: bg,
    color: type === 'error' ? C.error : type === 'success' ? C.success : C.accent,
    border: `1px solid ${border}`,
  };
}

function getStrength(pw: string): { score: number; label: string; color: string } {
  if (!pw) return { score: 0, label: '', color: 'transparent' };
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  if (s <= 1) return { score: s, label: 'Weak',   color: C.error };
  if (s <= 3) return { score: s, label: 'Fair',   color: C.warn };
  return           { score: s, label: 'Strong', color: C.success };
}

function GoogleIcon(): React.ReactElement {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

interface FormValues { name: string; email: string; password: string; }
interface MessageState { text: string; type: 'error' | 'success' | 'info'; }

export function SignupForm(): React.ReactElement {
  const closeModal = useAuthStore((s) => s.closeAuthModal);
  const setTab     = useAuthStore((s) => s.setAuthTab);

  const [message, setMessage]               = useState<MessageState | null>(null);
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [nameFocus,  setNameFocus]  = useState(false);
  const [emailFocus, setEmailFocus] = useState(false);
  const [pwFocus,    setPwFocus]    = useState(false);
  const [pwValue,    setPwValue]    = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>();
  const showMsg = (text: string, type: MessageState['type']) => setMessage({ text, type });

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true); setMessage(null);
    try {
      await handleSignup(values.name.trim(), values.email, values.password);
      showMsg('Account created successfully! Welcome to ESP for Sports.', 'success');
      setTimeout(() => closeModal(), 1_500);
    } catch (err) {
      showMsg(getAuthErrorMessage(err), 'error');
    } finally { setIsSubmitting(false); }
  };

  const onGoogle = async () => {
    setIsGoogleLoading(true); setMessage(null);
    try {
      const { isNewUser, displayName } = await handleGoogleSignIn();
      showMsg(isNewUser ? `Welcome to ESP for Sports, ${displayName}!` : `Welcome back, ${displayName}!`, 'success');
      setTimeout(() => closeModal(), 900);
    } catch (err) {
      const result = handleGoogleError(err);
      if (!result.handled) showMsg(result.message ?? 'Google sign-in failed. Please try again.', 'error');
    } finally { setIsGoogleLoading(false); }
  };

  const strength = getStrength(pwValue);

  const focusGlow = (active: boolean): React.CSSProperties => active
    ? { borderColor: C.accent, boxShadow: '0 0 0 3px rgba(79,140,247,0.12)' }
    : {};

  return (
    <form style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '20px' }} onSubmit={handleSubmit(onSubmit)} noValidate>
      {message && <div style={msgStyle(message.type)} role="alert">{message.text}</div>}

      {/* Full name */}
      <div>
        <label style={{ fontSize: '0.72rem', color: C.muted, marginBottom: '6px', display: 'block', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }} htmlFor="signupName">
          Full name
        </label>
        <input
          id="signupName" type="text" autoComplete="name"
          style={{ ...INPUT_BASE, borderColor: errors.name ? C.error : 'rgba(255,255,255,0.08)', ...focusGlow(nameFocus) }}
          onFocus={() => setNameFocus(true)}
          {...register('name', { required: 'Name is required', onBlur: () => setNameFocus(false) })}
        />
        {errors.name && <p style={{ fontSize: '0.78rem', color: C.error, marginTop: '4px' }}>{errors.name.message}</p>}
      </div>

      {/* Email */}
      <div>
        <label style={{ fontSize: '0.72rem', color: C.muted, marginBottom: '6px', display: 'block', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }} htmlFor="signupEmail">
          Email address
        </label>
        <input
          id="signupEmail" type="email" autoComplete="email"
          style={{ ...INPUT_BASE, borderColor: errors.email ? C.error : 'rgba(255,255,255,0.08)', ...focusGlow(emailFocus) }}
          onFocus={() => setEmailFocus(true)}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            onBlur: () => setEmailFocus(false),
          })}
        />
        {errors.email && <p style={{ fontSize: '0.78rem', color: C.error, marginTop: '4px' }}>{errors.email.message}</p>}
      </div>

      {/* Password + strength */}
      <div>
        <label style={{ fontSize: '0.72rem', color: C.muted, marginBottom: '6px', display: 'block', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }} htmlFor="signupPassword">
          Password
        </label>
        <input
          id="signupPassword" type="password" autoComplete="new-password"
          style={{ ...INPUT_BASE, borderColor: errors.password ? C.error : 'rgba(255,255,255,0.08)', ...focusGlow(pwFocus) }}
          onFocus={() => setPwFocus(true)}
          {...register('password', {
            required: 'Password is required',
            minLength: { value: 8, message: 'Password must be at least 8 characters' },
            onChange: (e) => setPwValue(e.target.value),
            onBlur: () => setPwFocus(false),
          })}
        />
        {pwValue.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <div style={{
              height: '3px', borderRadius: '2px',
              background: strength.color,
              width: `${(strength.score / 4) * 100}%`,
              transition: 'width 0.3s ease, background 0.3s ease',
            }} />
            <span style={{ fontSize: '0.75rem', color: strength.color, marginTop: '4px', display: 'block' }}>
              {strength.label}
            </span>
          </div>
        )}
        {errors.password && <p style={{ fontSize: '0.78rem', color: C.error, marginTop: '4px' }}>{errors.password.message}</p>}
      </div>

      {/* Submit */}
      <button
        type="submit" disabled={isSubmitting}
        style={{
          width: '100%', padding: '13px', borderRadius: '999px',
          background: 'linear-gradient(135deg, #4f8cf7 0%, #6366f1 100%)',
          border: 'none', color: '#fff', fontSize: '0.95rem',
          fontWeight: 700, cursor: isSubmitting ? 'not-allowed' : 'pointer',
          opacity: isSubmitting ? 0.7 : 1,
          transition: 'opacity 0.15s', fontFamily: 'inherit',
          letterSpacing: '0.04em',
        }}
      >
        {isSubmitting ? 'Creating account…' : 'Create Account'}
      </button>

      {/* Divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: C.muted, fontSize: '0.82rem' }}>
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
        <span>or</span>
        <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
      </div>

      {/* Google */}
      <button
        id="googleSignUpBtn" type="button" disabled={isGoogleLoading}
        style={{
          width: '100%', padding: '12px',
          background: '#fff',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '999px', color: '#1e293b',
          fontSize: '0.95rem', fontWeight: 600,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
          opacity: isGoogleLoading ? 0.7 : 1,
          transition: 'opacity 0.15s', fontFamily: 'inherit',
        }}
        onClick={onGoogle}
      >
        <GoogleIcon />
        {isGoogleLoading ? 'Connecting…' : 'Sign up with Google'}
      </button>

      {/* Switch */}
      <p style={{ textAlign: 'center', color: C.muted, fontSize: '0.88rem', margin: 0 }}>
        Already have an account?{' '}
        <button type="button" style={{ background: 'none', border: 'none', color: C.accent, fontSize: '0.88rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }} onClick={() => setTab('login')}>
          Sign in
        </button>
      </p>
    </form>
  );
}
